import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { Mic, Stop, Pause, PlayArrow, Delete, Close } from '@mui/icons-material';

const AudioRecorder = forwardRef(({ onSave, onRecordingStart, onRecordingEnd, onCancel, autoStart = false }, ref) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [previewAudio, setPreviewAudio] = useState(null);
  const [isClosed, setIsClosed] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const waveformRef = useRef(null);
  const cursorRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const waveformHistoryRef = useRef([]);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const sourceRef = useRef(null);
  const audioPlayerRef = useRef(new Audio());
  const audioWaitersRef = useRef([]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const updateCursorPosition = (time) => {
    if (!cursorRef.current || !waveformRef.current || !duration) return;
    const container = waveformRef.current;
    const cursor = cursorRef.current;
    const width = container.offsetWidth;
    const left = Math.max(0, Math.min(width, (time / duration) * width));
    cursor.style.left = `${left}px`;
  };

  const handlePlayPause = () => {
    if (!previewAudio) return;
    if (isPlaying) {
      audioPlayerRef.current.pause();
      setIsPlaying(false);
    } else {
      audioPlayerRef.current.play().catch(() => setIsPlaying(false));
      setIsPlaying(true);
    }
  };

  const drawWaveform = () => {
    if (!analyserRef.current) return;
    const canvas = waveformRef.current;
    const ctx = canvas.getContext('2d');
    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);

    if (!isPaused) {
      const average = Array.from(dataArray).reduce((a, b) => a + b, 0) / dataArray.length;
      waveformHistoryRef.current.push(average);
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const history = waveformHistoryRef.current;
    if (!history.length) return;

    const totalWidth = history.length * 3;
    const scale = canvas.width / totalWidth;
    history.forEach((value, index) => {
      const x = index * 3 * scale;
      const barWidth = 2 * scale;
      const barHeight = (value / 255) * (canvas.height / 1.5);
      const centerY = canvas.height / 2;
      ctx.fillStyle = '#78716c';
      const radius = barWidth / 2;
      ctx.beginPath();
      ctx.moveTo(x + radius, centerY - barHeight);
      ctx.lineTo(x + barWidth - radius, centerY - barHeight);
      ctx.quadraticCurveTo(x + barWidth, centerY - barHeight, x + barWidth, centerY - barHeight + radius);
      ctx.lineTo(x + barWidth, centerY + barHeight - radius);
      ctx.quadraticCurveTo(x + barWidth, centerY + barHeight, x + barWidth - radius, centerY + barHeight);
      ctx.lineTo(x + radius, centerY + barHeight);
      ctx.quadraticCurveTo(x, centerY + barHeight, x, centerY + barHeight - radius);
      ctx.lineTo(x, centerY - barHeight + radius);
      ctx.quadraticCurveTo(x, centerY - barHeight, x + radius, centerY - barHeight);
      ctx.closePath();
      ctx.fill();
    });

    if (!isPaused) {
      animationFrameRef.current = requestAnimationFrame(drawWaveform);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      let recorder;
      if (window.MediaRecorder && MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
        recorder = new MediaRecorder(stream, { mimeType: 'audio/ogg;codecs=opus' });
      } else if (window.MediaRecorder) {
        recorder = new MediaRecorder(stream);
      } else {
        throw new Error('MediaRecorder não suportado neste navegador');
      }

      chunksRef.current = [];
      waveformHistoryRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const mimeType = recorder.mimeType || 'audio/ogg';
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64Audio = reader.result;
          setPreviewAudio(base64Audio);
          if (audioWaitersRef.current.length) {
            audioWaitersRef.current.forEach(resolve => resolve(base64Audio));
            audioWaitersRef.current = [];
          }
        };
        reader.readAsDataURL(blob);
      };

      mediaRecorderRef.current = recorder;
      recorder.start();

      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.3;
      source.connect(analyser);
      analyserRef.current = analyser;
      audioContextRef.current = audioCtx;
      sourceRef.current = source;

      const canvas = waveformRef.current;
      const resizeCanvas = () => {
        const container = canvas.parentElement;
        canvas.width = container.clientWidth;
        canvas.height = 60;
      };
      resizeCanvas();
      window.addEventListener('resize', resizeCanvas);

      drawWaveform();
      onRecordingStart && onRecordingStart();
      setIsRecording(true);
      setIsPaused(false);
    } catch (err) {
      console.error('Erro ao acessar microfone:', err);
    }
  };

  const pauseRecording = () => {
    if (!mediaRecorderRef.current) return;
    if (isPaused) {
      mediaRecorderRef.current.resume();
      if (audioContextRef.current) audioContextRef.current.resume();
      drawWaveform();
      setIsPaused(false);
    } else {
      mediaRecorderRef.current.pause();
      if (audioContextRef.current) audioContextRef.current.suspend();
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      setIsPaused(true);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(() => {});
    }
    if (sourceRef.current) sourceRef.current.disconnect();
    setIsRecording(false);
    setIsPaused(false);
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    waveformHistoryRef.current = [];
    onRecordingEnd && onRecordingEnd();
  };

  const handleSave = () => {
    if (isRecording) stopRecording();
    if (previewAudio) onSave && onSave(previewAudio);
  };

  const resetRecording = () => {
    setPreviewAudio(null);
    chunksRef.current = [];
    mediaRecorderRef.current = null;
    setIsRecording(false);
    setIsPaused(false);
    setIsClosed(true);
    waveformHistoryRef.current = [];
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    onCancel && onCancel();
  };

  useEffect(() => {
    if (autoStart) {
      startRecording();
      setIsClosed(false);
    }
  }, [autoStart]);

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (mediaRecorderRef.current) mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(() => {});
      }
    };
  }, []);

  useEffect(() => {
    if (previewAudio) {
      const audio = audioPlayerRef.current;
      audio.src = previewAudio;
      const handleLoadedMetadata = () => {
        if (isFinite(audio.duration)) {
          setDuration(audio.duration);
          setIsPlaying(false);
        }
      };
      const handleTimeUpdate = () => {
        if (isFinite(audio.currentTime)) {
          setCurrentTime(audio.currentTime);
          updateCursorPosition(audio.currentTime);
        }
      };
      const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
        updateCursorPosition(0);
      };
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('ended', handleEnded);
      return () => {
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('ended', handleEnded);
        audio.pause();
        audio.src = '';
      };
    }
  }, [previewAudio]);

  useImperativeHandle(ref, () => ({
    getCurrentAudio: () => previewAudio,
    getAudioAfterStop: () => {
      if (previewAudio) return Promise.resolve(previewAudio);
      if (isRecording && mediaRecorderRef.current) {
        return new Promise((resolve) => {
          audioWaitersRef.current.push(resolve);
          stopRecording();
        });
      }
      return Promise.resolve(null);
    },
    resetRecording,
    handleSave,
    stopRecording
  }));

  return (
    <Box sx={{
      p: 1.5,
      borderRadius: '12px',
      backgroundColor: 'var(--surface-elevated)',
      boxShadow: 'var(--shadow-sm)',
      display: 'flex',
      flexDirection: 'column',
      gap: 1.5,
      width: '100%',
      border: '1px solid var(--border)',
      transition: 'border-color 0.2s ease',
    }}>
      <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ width: '100%', paddingRight: '8px' }}>
        <IconButton
          onClick={resetRecording}
          sx={{
            backgroundColor: 'var(--surface-muted)',
            color: 'var(--text-secondary)',
            '&:hover': {
              backgroundColor: 'var(--border)',
            },
            transition: 'background-color 0.15s ease',
            width: 40,
            height: 40,
          }}
        >
          {isClosed ? <Close fontSize="small" /> : <Delete fontSize="small" />}
        </IconButton>

        <Box sx={{
          flexGrow: 1,
          maxWidth: 'calc(100% - 180px)',
          height: '70px',
          backgroundColor: 'var(--surface-muted)',
          borderRadius: '8px',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          border: isRecording ? '2px solid var(--border-strong)' : '1px solid var(--border)',
          transition: 'border-color 0.2s ease',
          boxShadow: 'none',
        }}>
          <canvas ref={waveformRef} style={{ width: '100%', height: '100%', display: 'block', cursor: previewAudio ? 'pointer' : 'default' }} />
          {previewAudio && (
            <Box 
              ref={cursorRef} 
              sx={{ 
                position: 'absolute', 
                top: '50%', 
                transform: 'translate(-50%, -50%)', 
                width: 3, 
                height: '80%', 
                backgroundColor: 'var(--accent)',
                pointerEvents: 'none',
                transition: 'left 0.05s linear',
                borderRadius: '2px',
                zIndex: 1
              }}
            />
          )}
          {isRecording && !isPaused && (
            <Box sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              backgroundColor: 'var(--surface-elevated)',
              color: 'var(--text-secondary)',
              padding: '4px 8px',
              borderRadius: '12px',
              fontSize: '0.7rem',
              fontWeight: 600,
              border: '1px solid var(--border)',
              animation: 'pulse 1.5s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.85 }
              }
            }}>
              <Box sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: 'var(--accent)',
                animation: 'blink 1s ease-in-out infinite',
                '@keyframes blink': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.35 }
                }
              }} />
              Gravando
            </Box>
          )}
        </Box>

        {!isRecording && !previewAudio && (
          <IconButton
            onClick={startRecording}
            sx={{
              backgroundColor: 'var(--accent)',
              color: '#fafaf9',
              '&:hover': {
                backgroundColor: 'var(--accent-hover)',
              },
              transition: 'background-color 0.15s ease',
              width: 48,
              height: 48,
              boxShadow: 'none',
            }}
          >
            <Mic />
          </IconButton>
        )}
        {isRecording && (
          <>
            <IconButton
              onClick={pauseRecording}
              sx={{
                backgroundColor: isPaused ? 'var(--accent)' : 'var(--surface-muted)',
                color: isPaused ? '#fafaf9' : 'var(--text-primary)',
                border: isPaused ? 'none' : '1px solid var(--border)',
                '&:hover': {
                  backgroundColor: isPaused ? 'var(--accent-hover)' : 'var(--border)',
                },
                transition: 'background-color 0.15s ease',
                width: 40,
                height: 40,
                boxShadow: 'none',
              }}
            >
              {isPaused ? <PlayArrow fontSize="small" /> : <Pause fontSize="small" />}
            </IconButton>
            <IconButton
              onClick={stopRecording}
              sx={{
                backgroundColor: 'var(--text-primary)',
                color: '#fafaf9',
                '&:hover': {
                  backgroundColor: 'var(--accent)',
                },
                transition: 'background-color 0.15s ease',
                width: 40,
                height: 40,
                boxShadow: 'none',
              }}
            >
              <Stop fontSize="small" />
            </IconButton>
          </>
        )}
        {previewAudio && !isRecording && (
          <>
            <IconButton
              onClick={handlePlayPause}
              sx={{
                backgroundColor: 'var(--accent)',
                color: '#fafaf9',
                '&:hover': {
                  backgroundColor: 'var(--accent-hover)',
                },
                transition: 'background-color 0.15s ease',
                width: 40,
                height: 40,
                boxShadow: 'none',
              }}
            >
              {isPlaying ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
            </IconButton>
            <Box sx={{
              minWidth: '50px',
              textAlign: 'center',
              padding: '0 8px'
            }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'var(--text-muted)',
                  fontWeight: '500',
                  fontSize: '0.75rem'
                }}
              >
                {isPlaying ? formatTime(currentTime) : formatTime(duration)}
              </Typography>
            </Box>
          </>
        )}
      </Stack>
    </Box>
  );
});

export default AudioRecorder;


